import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, MapPin, Send, Image as ImageIcon, MessageCircle,
  Radio, CheckCircle2, Activity, Route as RouteIcon,
} from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import api from '@/lib/api';

const TRACKING_POLL_MS = 8000;
const MSG_POLL_MS = 5000;
const TRAILS_POLL_MS = 15000;

const photographerColors = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899',
  '#14b8a6', '#f97316', '#6366f1', '#06b6d4',
];
function colorForPhotographer(id, list) {
  if (!id || !list) return photographerColors[0];
  const idx = list.findIndex((p) => p.id === id);
  return photographerColors[(idx >= 0 ? idx : 0) % photographerColors.length];
}

function shortTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return shortTime(iso);
}

export default function LiveMasterControl({ user }) {
  const { eventId, planId } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedPin, setSelectedPin] = useState(null);
  const [showTrails, setShowTrails] = useState(true);
  const [trails, setTrails] = useState({});
  const [hoveredPid, setHoveredPid] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const messagesScrollerRef = useRef(null);
  const lastMsgTsRef = useRef(null);
  const fileInputRef = useRef(null);

  /* ---------- Polling: tracking + context ---------- */
  const hasLoadedRef = useRef(false);
  const fetchTracking = useCallback(async () => {
    try {
      const r = await api.get(`/share/plan/${planId}/tracking`, {
        params: { include_context: true },
      });
      setData(r.data);
      hasLoadedRef.current = true;
      setErr(null);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) setErr(t('liveControl.notFound', { defaultValue: 'Plan not found' }));
      else if (!hasLoadedRef.current) setErr(t('liveControl.loadError', { defaultValue: 'Could not load live data' }));
    }
  }, [planId, t]);

  useEffect(() => {
    fetchTracking();
    const id = setInterval(fetchTracking, TRACKING_POLL_MS);
    return () => clearInterval(id);
  }, [fetchTracking]);

  /* ---------- Polling: messages ---------- */
  const fetchMessages = useCallback(async () => {
    try {
      const params = lastMsgTsRef.current ? { since: lastMsgTsRef.current } : {};
      const r = await api.get(`/share/plan/${planId}/messages`, { params });
      const newMsgs = r.data || [];
      if (newMsgs.length) {
        setMessages((prev) => [...prev, ...newMsgs]);
        lastMsgTsRef.current = newMsgs[newMsgs.length - 1].created_at;
      }
    } catch {
      /* swallow */
    }
  }, [planId]);

  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, MSG_POLL_MS);
    return () => clearInterval(id);
  }, [fetchMessages]);

  /* ---------- Polling: trails ---------- */
  const fetchTrails = useCallback(async () => {
    if (!showTrails) return;
    try {
      const r = await api.get(`/share/plan/${planId}/trails`, { params: { minutes: 60 } });
      setTrails(r.data?.trails || {});
    } catch {
      /* swallow */
    }
  }, [planId, showTrails]);

  useEffect(() => {
    if (!showTrails) { setTrails({}); return; }
    fetchTrails();
    const id = setInterval(fetchTrails, TRAILS_POLL_MS);
    return () => clearInterval(id);
  }, [fetchTrails, showTrails]);

  useEffect(() => {
    if (messagesScrollerRef.current) {
      messagesScrollerRef.current.scrollTop = messagesScrollerRef.current.scrollHeight;
    }
  }, [messages]);

  /* ---------- Send chat ---------- */
  const sendMessage = async (overrides = {}) => {
    const body = {
      text: overrides.text !== undefined ? overrides.text : text.trim(),
      image_url: overrides.image_url || null,
    };
    if (!body.text && !body.image_url) return;
    setSending(true);
    try {
      const r = await api.post(`/share/plan/${planId}/messages`, body);
      setMessages((prev) => [...prev, r.data]);
      lastMsgTsRef.current = r.data.created_at;
      if (overrides.text === undefined) setText('');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || t('liveControl.sendError', { defaultValue: 'Could not send message' }));
    } finally {
      setSending(false);
    }
  };

  const onSendForm = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleAttachClick = () => { if (fileInputRef.current) fileInputRef.current.click(); };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('liveControl.imageTooLarge', { defaultValue: 'Image is too large (max 10MB)' }));
      e.target.value = '';
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      const up = await api.post(`/share/plan/${planId}/upload-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await sendMessage({ image_url: up.data.url, text: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('liveControl.uploadFailed', { defaultValue: 'Upload failed' }));
    } finally {
      e.target.value = '';
    }
  };

  /* ---------- Derived ---------- */
  const live = data?.live || [];
  const arrivals = data?.arrivals || [];
  const spots = data?.spots || [];
  const photographers = data?.photographers || [];
  const courseTracks = data?.course_tracks || [];

  const photoById = useMemo(() => {
    const m = {};
    photographers.forEach((p) => { m[p.id] = p; });
    return m;
  }, [photographers]);

  const spotById = useMemo(() => {
    const m = {};
    spots.forEach((s) => { m[s.id] = s; });
    return m;
  }, [spots]);

  const spotsWithCoords = useMemo(
    () => spots.filter((s) => s.lat != null && s.lng != null),
    [spots],
  );

  const mapCenter = useMemo(() => {
    if (live[0]) return { lat: live[0].lat, lng: live[0].lng };
    if (spotsWithCoords[0]) return { lat: spotsWithCoords[0].lat, lng: spotsWithCoords[0].lng };
    return { lat: -33.8688, lng: 151.2093 };
  }, [live, spotsWithCoords]);

  // Sorted activity feed: arrivals + checkouts + recent live pings
  const feed = useMemo(() => {
    const items = [];
    arrivals.forEach((a) => {
      items.push({
        ts: a.arrived_at || a.recorded_at,
        kind: 'arrival',
        photographer: a.photographer_name || photoById[a.photographer_id]?.name || '—',
        spot: a.spot_name || spotById[a.spot_id]?.name || '—',
        photographerId: a.photographer_id,
        lat: a.lat,
        lng: a.lng,
      });
      if (a.departed_at) {
        items.push({
          ts: a.departed_at,
          kind: 'departure',
          photographer: a.photographer_name || photoById[a.photographer_id]?.name || '—',
          spot: a.spot_name || spotById[a.spot_id]?.name || '—',
          photographerId: a.photographer_id,
          lat: a.lat,
          lng: a.lng,
        });
      }
    });
    return items.sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
  }, [arrivals, photoById, spotById]);

  if (err && !data) {
    return (
      <div className="flex min-h-screen">
        <Sidebar user={user} />
        <div className="main-content flex-1 p-8">
          <Link to={`/events/${eventId}/plan/${planId}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-600 mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to plan
          </Link>
          <Card><CardContent className="p-8 text-center text-slate-600">{err}</CardContent></Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen">
        <Sidebar user={user} />
        <div className="main-content flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
        </div>
      </div>
    );
  }

  const planName = data.plan?.name || '';
  const eventName = data.event?.name || '';

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} />
      <div className="main-content flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2">
          <Link to={`/events/${eventId}/plan/${planId}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-600">
            <ChevronLeft className="w-4 h-4" /> {t('liveControl.backToPlan', { defaultValue: 'Plan' })}
          </Link>
          <div className="ml-2 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate">
              {t('liveControl.title', { defaultValue: 'Live Track' })} · {planName}
            </h1>
            <p className="text-xs text-slate-500">{eventName} · {t('liveControl.lastUpdated', { defaultValue: 'Updated' })} {shortTime(data.server_time)}</p>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 rounded-full">
              <Radio className="w-3 h-3" /> {live.length} {t('liveControl.online', { defaultValue: 'online' })}
            </span>
            <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> {arrivals.filter((a) => !a.departed_at).length} {t('liveControl.onSite', { defaultValue: 'on site' })}
            </span>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-3 p-3 overflow-hidden">
          {/* MAP */}
          <div className="lg:col-span-7 min-h-[400px]">
            <Card className="h-full overflow-hidden">
              <CardContent className="p-1 h-full">
                <div className="px-2 py-1.5 flex items-center gap-2 text-xs">
                  <Switch checked={showTrails} onCheckedChange={setShowTrails} id="trails-toggle" />
                  <Label htmlFor="trails-toggle" className="cursor-pointer flex items-center gap-1">
                    <RouteIcon className="w-3.5 h-3.5 text-emerald-600" />
                    {t('liveControl.showTrails', { defaultValue: 'Show movement trails (last 60 min)' })}
                  </Label>
                </div>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: 'calc(100vh - 220px)', minHeight: 400 }}
                  center={mapCenter}
                  zoom={spotsWithCoords.length === 1 ? 15 : 13}
                  onLoad={(m) => setMapRef(m)}
                  options={{
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: true,
                  }}
                >
                  {courseTracks
                    .filter((ct) => !ct.is_floor_plan
                      && Array.isArray(ct.track_points)
                      && ct.track_points.length > 1
                      && Number.isFinite(ct.track_points[0]?.lat))
                    .map((ct) => (
                      <Polyline
                        key={ct.id}
                        path={ct.track_points.map((p) => ({ lat: p.lat, lng: p.lng }))}
                        options={{ strokeColor: ct.color || '#0ea5e9', strokeWeight: 3, strokeOpacity: 0.7, clickable: false }}
                      />
                    ))}
                  {/* Photographer trails (movement history) */}
                  {showTrails && Object.entries(trails).map(([pid, points]) => {
                    const valid = (points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
                    if (valid.length < 2) return null;
                    const color = colorForPhotographer(pid, photographers);
                    return (
                      <Polyline
                        key={`trail-${pid}`}
                        path={valid.map((p) => ({ lat: p.lat, lng: p.lng }))}
                        options={{
                          strokeColor: color,
                          strokeWeight: 3,
                          strokeOpacity: 0.6,
                          clickable: false,
                          icons: [{
                            icon: { path: window.google?.maps?.SymbolPath?.FORWARD_OPEN_ARROW || 1, scale: 2, strokeColor: color },
                            offset: '50%',
                          }],
                        }}
                      />
                    );
                  })}
                  {/* Spot pins */}
                  {spotsWithCoords.map((s, i) => (
                    <Marker
                      key={`spot-${s.id}`}
                      position={{ lat: s.lat, lng: s.lng }}
                      label={{ text: String(i + 1), color: '#fff', fontWeight: 'bold', fontSize: '11px' }}
                      onClick={() => setSelectedPin({ kind: 'spot', spot: s })}
                    />
                  ))}
                  {/* Live photographer pins */}
                  {live.map((loc) => {
                    const color = colorForPhotographer(loc.photographer_id, photographers);
                    const isHovered = hoveredPid === loc.photographer_id;
                    return (
                      <Marker
                        key={`live-${loc.photographer_id}`}
                        position={{ lat: loc.lat, lng: loc.lng }}
                        icon={{
                          path: window.google?.maps?.SymbolPath?.CIRCLE,
                          scale: isHovered ? 16 : 10,
                          fillColor: color,
                          fillOpacity: 0.95,
                          strokeColor: isHovered ? '#0ea5e9' : '#fff',
                          strokeWeight: isHovered ? 4 : 3,
                        }}
                        zIndex={isHovered ? 9999 : 1}
                        title={loc.photographer_name || photoById[loc.photographer_id]?.name || ''}
                        onClick={() => setSelectedPin({ kind: 'live', loc })}
                      />
                    );
                  })}
                  {/* Arrival markers */}
                  {arrivals.filter((a) => Number.isFinite(a.lat)).map((a) => (
                    <Marker
                      key={`arr-${a.id || a.spot_id + a.photographer_id}`}
                      position={{ lat: a.lat, lng: a.lng }}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW,
                        scale: 5,
                        fillColor: a.departed_at ? '#94a3b8' : '#f59e0b',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 1,
                      }}
                      onClick={() => setSelectedPin({ kind: 'arrival', a })}
                    />
                  ))}
                  {selectedPin?.kind === 'live' && selectedPin.loc && (
                    <InfoWindow
                      position={{ lat: selectedPin.loc.lat, lng: selectedPin.loc.lng }}
                      onCloseClick={() => setSelectedPin(null)}
                    >
                      <div className="text-xs">
                        <p className="font-semibold">{selectedPin.loc.photographer_name || photoById[selectedPin.loc.photographer_id]?.name || '—'}</p>
                        <p className="text-slate-500">{relativeTime(selectedPin.loc.recorded_at)}</p>
                        {selectedPin.loc.accuracy_m && <p className="text-slate-400">±{Math.round(selectedPin.loc.accuracy_m)}m</p>}
                      </div>
                    </InfoWindow>
                  )}
                  {selectedPin?.kind === 'spot' && (
                    <InfoWindow
                      position={{ lat: selectedPin.spot.lat, lng: selectedPin.spot.lng }}
                      onCloseClick={() => setSelectedPin(null)}
                    >
                      <div className="text-xs">
                        <p className="font-semibold">{selectedPin.spot.name}</p>
                        {selectedPin.spot.address && <p className="text-slate-500">{selectedPin.spot.address}</p>}
                      </div>
                    </InfoWindow>
                  )}
                  {selectedPin?.kind === 'arrival' && (
                    <InfoWindow
                      position={{ lat: selectedPin.a.lat, lng: selectedPin.a.lng }}
                      onCloseClick={() => setSelectedPin(null)}
                    >
                      <div className="text-xs">
                        <p className="font-semibold">{selectedPin.a.photographer_name || '—'}</p>
                        <p className="text-slate-600">{selectedPin.a.spot_name}</p>
                        <p className="text-slate-500">
                          {t('liveControl.arrived', { defaultValue: 'Arrived' })}: {shortTime(selectedPin.a.arrived_at)}
                          {selectedPin.a.departed_at && ` → ${shortTime(selectedPin.a.departed_at)}`}
                        </p>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: photographers + feed + chat */}
          <div className="lg:col-span-5 flex flex-col gap-3 min-h-0">
            {/* Photographers list — hover to highlight on map */}
            <Card>
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <Radio className="w-4 h-4 text-emerald-600" />
                <h2 className="font-semibold text-sm text-slate-800">{t('liveControl.photographers', { defaultValue: 'Photographers' })}</h2>
                <Badge className="ml-auto text-[10px] bg-slate-200 text-slate-700 hover:bg-slate-200">{photographers.length}</Badge>
              </div>
              <div className="p-2 flex flex-wrap gap-1.5">
                {photographers.length === 0 ? (
                  <p className="text-xs text-slate-400 px-1">—</p>
                ) : (
                  photographers.map((p) => {
                    const isOnline = !!live.find((l) => l.photographer_id === p.id);
                    const color = colorForPhotographer(p.id, photographers);
                    const isHovered = hoveredPid === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onMouseEnter={() => {
                          setHoveredPid(p.id);
                          const loc = live.find((l) => l.photographer_id === p.id);
                          if (loc && mapRef?.panTo) {
                            mapRef.panTo({ lat: loc.lat, lng: loc.lng });
                          }
                        }}
                        onMouseLeave={() => setHoveredPid(null)}
                        onClick={() => {
                          const loc = live.find((l) => l.photographer_id === p.id);
                          if (loc) {
                            setSelectedPin({ kind: 'live', loc });
                            if (mapRef?.panTo) {
                              mapRef.panTo({ lat: loc.lat, lng: loc.lng });
                              mapRef.setZoom(17);
                            }
                          }
                        }}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs transition ${
                          isHovered
                            ? 'bg-blue-50 border-blue-400'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                        <span className="font-medium">{p.name}</span>
                        {isOnline && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Online" />}
                      </button>
                    );
                  })
                )}
              </div>
            </Card>

            {/* Break coverage roll-up across the whole event */}
            {(() => {
              const allCoverage = [];
              spots.forEach((s) => {
                (s.break_coverage || []).forEach((c) => {
                  allCoverage.push({ ...c, spot_id: s.id, spot_name: s.name });
                });
              });
              if (allCoverage.length === 0) return null;
              const nameOf = (id) => photographers.find((p) => p.id === id)?.name || '—';
              return (
                <Card>
                  <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                    <span>🔁</span>
                    <h2 className="font-semibold text-sm text-slate-800">{t('liveControl.breakCoverage', { defaultValue: 'Break coverage' })}</h2>
                    <Badge className="ml-auto text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">{allCoverage.length}</Badge>
                  </div>
                  <div className="p-2 space-y-1 max-h-40 overflow-y-auto">
                    {allCoverage
                      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                      .map((c) => (
                        <div
                          key={c.id}
                          className="text-xs flex items-start gap-2 p-2 rounded hover:bg-amber-50 cursor-pointer"
                          onMouseEnter={() => setHoveredPid(c.covering_photographer_id)}
                          onMouseLeave={() => setHoveredPid(null)}
                        >
                          <span className="w-1.5 h-1.5 rounded-full mt-1.5 bg-amber-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-800">
                              <b>{nameOf(c.covering_photographer_id)}</b>{' '}
                              <span className="text-slate-500">covers</span>{' '}
                              {c.covers_for_id ? <>for <b>{nameOf(c.covers_for_id)}</b> at </> : 'at '}
                              <b>{c.spot_name}</b>
                            </p>
                            <p className="text-[10px] text-slate-500">
                              {c.start_time}–{c.end_time}
                              {c.note && <> · {c.note}</>}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </Card>
              );
            })()}

            {/* Activity feed */}
            <Card className="flex-1 min-h-[200px] overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-600" />
                <h2 className="font-semibold text-sm text-slate-800">{t('liveControl.activity', { defaultValue: 'Activity' })}</h2>
                <Badge className="ml-auto text-[10px] bg-slate-200 text-slate-700 hover:bg-slate-200">{feed.length}</Badge>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {feed.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">{t('liveControl.noActivity', { defaultValue: 'No activity yet — waiting for photographers to check in.' })}</p>
                ) : (
                  feed.map((it, i) => (
                    <div
                      key={i}
                      className="text-xs flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                      onMouseEnter={() => setHoveredPid(it.photographerId)}
                      onMouseLeave={() => setHoveredPid(null)}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${it.kind === 'arrival' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800">
                          <span className="font-medium">{it.photographer}</span>
                          <span className="text-slate-500"> {it.kind === 'arrival' ? t('liveControl.arrivedAt', { defaultValue: 'arrived at' }) : t('liveControl.leftAt', { defaultValue: 'left' })} </span>
                          <span className="font-medium">{it.spot}</span>
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span>{shortTime(it.ts)} · {relativeTime(it.ts)}</span>
                          {Number.isFinite(it.lat) && Number.isFinite(it.lng) && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${it.lat},${it.lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-blue-600 hover:underline"
                              title="Open in Google Maps"
                            >
                              {it.lat.toFixed(5)}, {it.lng.toFixed(5)}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Chat */}
            <Card className="flex-1 min-h-[200px] overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                <h2 className="font-semibold text-sm text-slate-800">{t('liveControl.chat', { defaultValue: 'Team chat' })}</h2>
                <Badge className="ml-auto text-[10px] bg-slate-200 text-slate-700 hover:bg-slate-200">{messages.length}</Badge>
              </div>
              <div ref={messagesScrollerRef} className="flex-1 overflow-y-auto p-2 space-y-2">
                {messages.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">{t('liveControl.noMessages', { defaultValue: 'No messages yet.' })}</p>
                ) : (
                  messages.map((m) => {
                    const isCoord = m.sender_role === 'coordinator';
                    const isSelf = m.sender_id === user?.id;
                    const dotColor = isCoord ? '#0ea5e9' : colorForPhotographer(m.sender_id, photographers);
                    return (
                      <div key={m.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-xs ${
                            isSelf
                              ? 'bg-emerald-500 text-white'
                              : isCoord
                              ? 'bg-blue-50 border border-blue-200 text-slate-800'
                              : 'bg-white border border-slate-200 text-slate-800'
                          }`}
                        >
                          <p className="text-[10px] font-semibold opacity-80 mb-0.5 flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: dotColor }} />
                            {m.sender_name || '—'}
                            {isCoord && <span className="text-[9px] opacity-70">· coord</span>}
                          </p>
                          {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                          {m.image_url && (
                            <a href={m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`} target="_blank" rel="noreferrer">
                              <img
                                src={m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`}
                                alt=""
                                className="mt-1 max-h-32 rounded-md border border-slate-200"
                              />
                            </a>
                          )}
                          {m.location && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${m.location.lat},${m.location.lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className={`text-[11px] mt-1 inline-flex items-center gap-1 underline ${isSelf ? 'text-white' : 'text-blue-600'}`}
                            >
                              <MapPin className="w-3 h-3" />
                              {m.location.label || 'Location'}
                            </a>
                          )}
                          <p className={`text-[9px] mt-0.5 ${isSelf ? 'text-emerald-50' : 'text-slate-400'}`}>{shortTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <form onSubmit={onSendForm} className="p-1.5 border-t border-slate-200 flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleAttachClick}
                  className="px-2"
                  title={t('liveControl.attachImage', { defaultValue: 'Attach image' })}
                >
                  <ImageIcon className="w-4 h-4" />
                </Button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('liveControl.messagePlaceholder', { defaultValue: 'Message your team…' })}
                  maxLength={4000}
                  className="flex-1 h-8 px-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-xs"
                />
                <Button type="submit" size="sm" disabled={sending || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700 px-2">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
